import {Plural, Trans} from '@lingui/macro';
import React, {FC, ReactNode, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {computeMultiSelectCheckboxState} from '../../../components/common/table/helpers/tableHelpers';
import {AsyncTableSource} from '../../../components/common/table/source/asyncTableSource';
import {TableMultiSelectHeaderRow} from '../../../components/common/table/tableMultiSelectHeaderRow';
import {TableRowRenderOptions} from '../../../components/common/table/tableRow';
import {VirtualTable, VirtualTableHandle} from '../../../components/common/table/virtualTable';
import {VirtualTableRowSiblingData} from '../../../components/common/table/virtualTableBase';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {CustomFieldResourceTypesEnum} from '../../../models/api/customFieldModel';
import {
  TableOffsetPaginationDataModel,
  TablePaginationTypesEnum,
} from '../../../models/table/tablePaginationDataModel';
import {SettingsCustomFieldsPagesEnum} from '../../../models/url/settingsUrlModel';
import {fetchCustomFieldsForSettingsTableAsyncAction} from '../../../store/panels/settings/settingsPanelActions';
import {getCustomFieldsBaseUrl} from '../customFieldsHelpers';
import {CustomFieldsResourceName} from '../list/customFieldsResourceName';
import {CustomFieldsTableActions} from './customFieldsTableActions';
import {buildDefaultFieldsForResourceType, customFieldsTableColumns} from './customFieldsTableConfig';
import {CustomFieldsTableRowContainer} from './customFieldsTableRowContainer';

/*
 * Constants.
 */

// This is to let the table know we have data loaded in the table.
const loadedPaginationData: TableOffsetPaginationDataModel = {
  type: TablePaginationTypesEnum.OFFSET,
  canLoadMore: false,
  offset: 0,
};

/*
 * Props.
 */

interface CustomFieldsTableProps {
  customFieldIds: ReadonlyArray<number>;
  selectedCustomFieldIds: ReadonlyArray<number>;
  resourceType: CustomFieldResourceTypesEnum;
  tabPage: SettingsCustomFieldsPagesEnum;
  companyCreatedAt: number;
  loadingStatus?: AsyncStatusesEnum;

  addSelectedCustomFieldIds: HandlerOf<ReadonlyArray<number>>;
  removeSelectedCustomFieldIds: HandlerOf<ReadonlyArray<number>>;
  navigateToLink: HandlerOf<string>;
  onSuccessfulDelete: HandlerOf<ReactNode>;
}

/*
 * Style.
 */

const StyledTableContainerDiv = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0 0 0 12px;
  position: relative;
`;

const StyledEmptyStateContainerDiv = styled.div`
  position: absolute;
  top: 80px;
  left: 0;
  right: 0;
  color: ${(p) => p.theme.greys.shade70};
  text-align: center;
`;

/*
 * Component.
 */

export const CustomFieldsTable: FC<CustomFieldsTableProps> = (props) => {
  const {
    customFieldIds,
    selectedCustomFieldIds,
    resourceType,
    tabPage,
    companyCreatedAt,
    loadingStatus,
    addSelectedCustomFieldIds,
    removeSelectedCustomFieldIds,
    navigateToLink,
    onSuccessfulDelete,
  } = props;

  const tableRef = useRef<VirtualTableHandle>();
  const [lastSelectedCardId, setLastSelectedCardId] = useState<number>();
  const localize = useLocalize();
  const isLoading = !loadingStatus || loadingStatus === AsyncStatusesEnum.LOADING;
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const dispatch = useAppDispatch();

  const defaultCustomFields = useMemo(
    () => buildDefaultFieldsForResourceType(resourceType, companyCreatedAt, localize),
    [companyCreatedAt, localize, resourceType],
  );
  const defaultCustomFieldIds = defaultCustomFields.map((customField) => customField.id);

  const source = useMemo(() => {
    setHasFetchedData(false);
    return new AsyncTableSource({
      fetcher: async ({cancelToken}) => {
        await dispatch(fetchCustomFieldsForSettingsTableAsyncAction(resourceType));
        cancelToken.throwIfRequested();

        // Since this table doesn't support pagination, we can just set this locally.
        setHasFetchedData(true);
      },
    });
  }, [dispatch, resourceType]);

  const renderRow = (
    customFieldId: number,
    options: TableRowRenderOptions,
    siblingData?: VirtualTableRowSiblingData<number>,
  ) => (
    <CustomFieldsTableRowContainer
      customFieldId={customFieldId}
      defaultCustomFields={defaultCustomFields}
      options={options}
      tableColumns={customFieldsTableColumns}
      isSelected={selectedCustomFieldIds.includes(customFieldId)}
      isInMultiSelectMode={selectedCustomFieldIds.length > 0}
      isPreviousSiblingSelected={selectedCustomFieldIds.includes(siblingData?.previous ?? -1)}
      isNextSiblingSelected={selectedCustomFieldIds.includes(siblingData?.next ?? -1)}
      onSelect={() => {
        navigateToLink(`${getCustomFieldsBaseUrl(tabPage)}/edit/${customFieldId}`);
      }}
      onMultiSelect={() => {
        addSelectedCustomFieldIds(findSelectedValues(customFieldId, lastSelectedCardId, customFieldIds));
      }}
      onCheckBoxToggle={(shouldSelect) => {
        if (shouldSelect) {
          addSelectedCustomFieldIds([customFieldId]);
          setLastSelectedCardId(customFieldId);
        } else {
          removeSelectedCustomFieldIds([customFieldId]);
        }
      }}
    />
  );

  const renderMultiSelectHeader = (onMultiSelectChange: HandlerOf<boolean>) => (
    <TableMultiSelectHeaderRow
      multiSelectStatus={computeMultiSelectCheckboxState(
        customFieldIds.length,
        selectedCustomFieldIds.length,
      )}
      renderTotalItemsLabel={() => (
        <Plural
          id="custom-fields-table-total-selected"
          value={selectedCustomFieldIds.length}
          one="1 field selected"
          other="# fields selected"
        />
      )}
      onMultiSelectChange={onMultiSelectChange}
    >
      <CustomFieldsTableActions tabPage={tabPage} onSuccessfulDelete={onSuccessfulDelete} />
    </TableMultiSelectHeaderRow>
  );

  const renderEmptyState = () => (
    <StyledEmptyStateContainerDiv>
      <Trans id="custom-fields-table-empty-state">
        You do not have any {CustomFieldsResourceName({tabPage})} custom fields.
      </Trans>
    </StyledEmptyStateContainerDiv>
  );

  return (
    <StyledTableContainerDiv>
      <VirtualTable<number>
        ref={tableRef}
        columns={customFieldsTableColumns}
        source={source}
        items={isLoading ? [] : [...defaultCustomFieldIds, ...customFieldIds]}
        paginationData={hasFetchedData ? loadedPaginationData : undefined}
        renderRow={renderRow}
        renderEmpty={renderEmptyState}
        shouldAllowMultiSelect
        renderMultiSelectHeader={renderMultiSelectHeader}
        isInMultiSelectMode={selectedCustomFieldIds.length > 0}
        isSourceUnstable
        onMultiSelectHeaderChange={(checked: boolean) => {
          if (checked) {
            addSelectedCustomFieldIds(customFieldIds);
          } else {
            removeSelectedCustomFieldIds(customFieldIds);
          }
        }}
      />
    </StyledTableContainerDiv>
  );
};

/*
 * Helpers.
 */

function findSelectedValues(
  currentCustomFieldId: number,
  lastSelectedCustomFieldId: number | undefined,
  customFieldIds: ReadonlyArray<number>,
) {
  if (!lastSelectedCustomFieldId) {
    return [];
  }
  const currentIndex = customFieldIds.indexOf(currentCustomFieldId);
  const previousIndex = customFieldIds.indexOf(lastSelectedCustomFieldId);

  if (currentIndex < previousIndex) {
    return customFieldIds.slice(currentIndex, previousIndex);
  }
  return customFieldIds.slice(previousIndex, currentIndex + 1);
}
